<template>
    <div>
        <v-tabs
			v-model="activeTab"
			class="mt-4"
			color="primary"
		>
			<v-tab
				v-for="tab in tabs"
				:key="tab.value"
			>
				{{ tab.label }}
			</v-tab>
		</v-tabs>

		<div class="products__panel">
		<v-tabs-items v-model="activeTab">
			<!-- Received Samples -->
			<v-tab-item>
				<v-card flat color="transparent">
					<v-card-text class="text-body-1">
						{{$T('stock-quantities')}}
						{{ $T('sort-by') }} :
						<v-radio-group
							v-model="sort"
							row
							hide-details
						>
							<v-radio
								:label="$T('inputs.name')"
								:value="0"
								class="mx-auto"
								color="success"
							/>
							<v-radio
								:label="$T('inputs.quantity')"
								:value="1"
								class="mx-auto"
								color="success"
							/>
						</v-radio-group>
					</v-card-text>
				</v-card>

				<v-container>
					<div v-if="!loading && sortedTransactions.length > 0">
						<v-row class="mx-auto mb-0" style="width: 95%">
							<v-col cols="9">
								<v-icon small class="primary--text text--lighten-2 me-1">mdi-pill</v-icon>
								<span class="text-capitalize primary--text text--darken-1">{{$T('sample-name')}}</span>
							</v-col>
							<v-col cols="3" class="text-center">{{$T('remaining')}}</v-col>
						</v-row>
						<sample-card
						v-for="transaction in sortedTransactions"
						:key="`${transaction.materialId}-${sort}`"
						:sample="transaction"
						class="mb-2"
						@on-swipe="fetchVisit()"
						/>
					</div>

					<div
					v-else-if="!loading && sortedTransactions.length === 0"
					class="d-flex flex-column justify-center align-center"
					style="height: 65vh"
					>
						<v-icon x-large class="mb-3">mdi-pill</v-icon>
						<span class="grey--text text--darken-2">{{$T('no-samples-to-show')}}</span>
					</div>

					<loader v-if="loading"/>
				</v-container>
			</v-tab-item>

			<!-- Unreceived Samples -->
			<v-tab-item>
				<v-container>
					<div v-if="!loading && tempTransactions.length > 0">
						<unreceived-sample-card
						v-for="transaction in tempTransactions"
						:key="transaction.id"
						:transaction="transaction"
						:accepting="acceptingId === transaction.id"
						class="mb-2"
						@accept="acceptTempTransaction"
						/>
					</div>

					<div
					v-else-if="!loading && tempTransactions.length === 0"
					class="d-flex flex-column justify-center align-center"
					style="height: 65vh"
					>
						<v-icon x-large class="mb-3">mdi-truck-delivery-outline</v-icon>
						<span class="grey--text text--darken-2">{{$T('no-unreceived-samples-to-show')}}</span>
					</div>

					<loader v-if="loading"/>
				</v-container>
			</v-tab-item>

			<!-- Products -->
			<v-tab-item>
				<v-card flat color="transparent">
					<v-card-text class="d-flex flex-wrap align-center">
						<v-autocomplete
							v-model="materialFormId"
							:label="$T('inputs.product-form')"
							:items="materialForms"
							item-text="name"
							item-value="id"
							color="primary"
							:disabled="loading"
							:loading="loading"
							flat
							dense
							clearable
							hide-details
							@change="filter"
						></v-autocomplete>
					</v-card-text>

					<v-card-text class="pt-2">
						<div class="d-flex align-center">
							<v-text-field
							v-model="name"
							:label="$T('inputs.product-name')"
							color="primary"
							prepend-icon="mdi-palette-swatch"
							dense
							outlined
							clearable
							hide-details
							@keypress.enter="filter(); isFilteredByName = true;"
							@click:clear="setFilterState"
							></v-text-field>
						</div>
					</v-card-text>
				</v-card>

				<v-container>
					<v-row v-if="!loading && materials.length > 0" dense>
						<v-col
							sm="5"
							cols="12"
							v-for="material in materials"
							:key="material.id"
							class="mb-4 mx-sm-auto"
						>
							<product-card
							:material="material"
							:disabled="disableCard"
							/>
						</v-col>
					</v-row>

					<div
					v-else-if="!loading && materials.length === 0"
					class="d-flex flex-column justify-center align-center"
					style="height: 45vh"
					>
						<v-icon x-large class="mb-3">mdi-flask-remove</v-icon>
						<span class="secondary--text text--darken-2">{{$T('no-materials-to-show')}}</span>
					</div>

					<loader v-if="loading"/>
				</v-container>
			</v-tab-item>
		</v-tabs-items>
		</div>
    </div>
</template>

<script>
import ProductCard from '../components/ProductCard'
import SampleCard from '../components/SampleCard'
import UnreceivedSampleCard from '../components/UnreceivedSampleCard'
import Loader from '../components/Loader'
import { mapState } from 'vuex'
export default {
	name: 'Products',

	components: {
		ProductCard,
		SampleCard,
		UnreceivedSampleCard,
		Loader
	},

	data() {
		return {
			activeTab: this.$route.query.tab === 'unreceived-samples' ? 1 : 0,

			loading: true,
			disableCard: false,
			isFilteredByName: false,

			name: null,
			materialFormId: null,

			sort: 1,
			sortedTransactions: [],

			acceptingId: null,
		}
	},

	watch: {
		transactions() {
			this.sortTransactions();
		},
		sort() {
			this.sortTransactions();
		}
	},

	computed: {
		...mapState({
			materials: state => state.materials.materials,
			materialForms: state => state.materialForms.materialForms,
			transactions: state => state.materialsTransactions.transactions,
			tempTransactions: state => state.materialsTransactions.tempTransactions
		}),

		tabs() {
			return [
				{ value: 0, label: this.$T('tab-received-samples') },
				{ value: 1, label: this.$T('tab-unreceived-samples') },
				{ value: 2, label: this.$T('tab-products') }
			]
		}
	},

	methods: {
		filter() {
			this.disableCard = true;

			this.$emit('loading:active');
			this.$store.dispatch('materials/fetchAll', {
				name: this.name,
				materialFormId: this.materialFormId
			})
				.finally(() => {
					this.$emit('loading:disable');
					this.disableCard = false;
				})
		},

		setFilterState() {
			if (this.isFilteredByName) {
				this.isFilteredByName = false;
				this.name = null;
				this.filter();
			}
		},

		sortTransactions() {
			const transactions = [...this.transactions];
			if (this.sort === 0) {
				this.sortedTransactions = transactions.sort((a, b) => {
					if (!a.material || !a.material.name) return 1;
					if (!b.material || !b.material.name) return -1;
					return a.material.name.localeCompare(b.material.name)
				});
			} else {
				if (this.transactions.length) {
					this.sortedTransactions = transactions?.sort((a, b) => b.remaining.amount - a.remaining.amount);
				}
			}
		},

		acceptTempTransaction(id) {
			this.acceptingId = id;

			this.$store.dispatch('materialsTransactions/acceptTempTransaction', { id })
				.then(() => {
					this.$eventBus.$emit('show-snackbar', this.$T('unreceived-sample-accepted'));
				})
				.finally(() => {
					this.acceptingId = null;
				})
		}
	},

	mounted() {
		Promise.all([
			this.$store.dispatch('materials/fetchAll', { name: null, materialFormId: null }),
			this.$store.dispatch('materialForms/fetchAll'),
			this.$store.dispatch('materialsTransactions/fetchAll'),
			this.$store.dispatch('materialsTransactions/fetchTempTransactions')
		])
			.finally(() => {
				this.loading = false;
			})
	},

	beforeDestroy() {
		this.$store.dispatch('materials/reset');
		this.$store.dispatch('materialForms/reset');
		this.$store.dispatch('materialsTransactions/reset');
		this.$store.dispatch('materialsTransactions/resetTempTransactions');
	}
}
</script>

<style scoped>
.products__panel {
	background-color: #fff;
}

/* Vuetify always reserves space for the v-tabs prev/next scroll hint on
   overflowing mobile tab bars, even when it stays invisible - this leaves
   a blank gap before the first tab. Hiding it keeps swipe-scrolling intact. */
::v-deep .v-slide-group__prev,
::v-deep .v-slide-group__next {
	display: none !important;
}
</style>
